import fallbackImage from "@assets/images/fallback-game.svg";
import {useGetTopGames} from "@hooks/gamesList/useGetTopGames";
import React from "react";
import {Trans, useTranslation} from "react-i18next";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {Carousel} from "@/components";
import {ImageWithLoadSpinner} from "@/components/image-with-load-spinner";
import {useAuthStore} from "@/store/useAuth.store";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";
import {getProviderDisplayName} from "@/util/providerNames";
import styles from "./top-games.styles.module.scss";

export const TopGames = ({provider}) => {
  const {t} = useTranslation();
  const location = useLocation();
  const {data} = useGetTopGames(provider && provider !== "all" ? {provider, limit: 10} : {limit: 10});
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const isProviderPage = provider && provider !== "all";
  const providerDisplayName = isProviderPage ? getProviderDisplayName(provider) : "";
  const viewAllLink = isProviderPage ? `/games/provider/${String(provider).toLowerCase()}#all-games` : "/gameslist/all";

  const initializeGames = useGameMetadataStore(state => state.initializeGames);
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const gamesLoading = useGameMetadataStore(state => state.loading);
  const gamesInitialized = useGameMetadataStore(state => state.initialized);

  React.useEffect(() => {
    if (!gamesInitialized && !gamesLoading) {
      initializeGames();
    }
  }, [gamesInitialized, gamesLoading, initializeGames]);
  const topGames = React.useMemo(() => {
    const listTopGames = data?.data?.data || [];

    if (gamesLoading || !gamesInitialized) {
      return [];
    }

    return listTopGames.map(topGame => {
      const game = getGameById(topGame?.game_id, topGame?.game_provider);

      return {
        ...topGame,
        icon: game?.icon || fallbackImage,
        provider: game?.provider || topGame?.game_provider,
        name: game?.name || topGame?.game_name,
        id: game?.id || topGame?.game_id || topGame?.id,
      };
    });
  }, [data, getGameById, gamesLoading, gamesInitialized]);

  const handleGameClick = React.useCallback(
    (provider, gameId) => {
      if (authStatus === "authenticated") {
        navigate(`/games/${provider}/${gameId}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  const handleViewAllClick = React.useCallback(
    event => {
      if (!viewAllLink.includes("#")) return;

      const [targetPath, targetId] = viewAllLink.split("#");
      if (!targetPath || !targetId) return;

      const targetHash = `#${targetId}`;
      if (location.pathname !== targetPath || location.hash !== targetHash) return;

      event.preventDefault();
      const target = document.getElementById(targetId);
      if (!target) return;

      const offset = 100;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({top, behavior: "smooth"});
    },
    [location.pathname, location.hash, viewAllLink]
  );

  return (
    <div
      id="topGames"
      className={styles.topGamCon}
    >
      <div className={styles.topGamHeaBox}>
        <div className={styles.topGamTitBox}>
          <Trans
            i18nKey={isProviderPage ? "top10GamesSubTitleMobileProvider" : "top10GamesSubTitleMobile"}
            values={isProviderPage ? {provider: providerDisplayName} : undefined}
            parent={props => (
              <p
                className={styles.titHeaTex}
                {...props}
              />
            )}
            components={{
              highlight: <span className={styles.numTop} />,
            }}
          />
        </div>
        <p className={styles.subTex}>
          {isProviderPage
            ? t("top10GamesSubTitleDesktopProvider", {provider: providerDisplayName})
            : t("top10GamesSubTitleDesktop")}
        </p>
        <Link
          to={viewAllLink}
          className={styles.viewAllBtn}
          onClick={handleViewAllClick}
        >
          {t("viewAll", "Ver todo")}
          <span className={styles.viewAllArrow}>›</span>
        </Link>
      </div>
      <Carousel
        renderItems={Array.from({length: 10}).map((_, idx) => {
          if (gamesLoading || !gamesInitialized || topGames.length === 0) {
            return (
              <div key={`loading-${idx++}`}>
                <div className={styles.topGamesCard}>
                  <div>
                    <img
                      src={fallbackImage}
                      alt="Loading..."
                    />
                    <span className={styles.carNum}>{idx + 1}</span>
                  </div>
                </div>
              </div>
            );
          }
          const game = topGames[idx % topGames.length];
          const realNumber = (idx % topGames.length) + 1;
          return (
            <div key={`${game.id || idx}-${idx}`}>
              <div
                className={styles.topGamesCard}
                onClick={() => handleGameClick(game.provider, game.id)}
              >
                <ImageWithLoadSpinner
                  src={game.icon || fallbackImage}
                  alt={game.name}
                  fallbackSrc={fallbackImage}
                  wrapperClassName={styles.topGamesThumb}
                  imgClassName={styles.topGamesCardImgInner}
                  objectFit="cover"
                  showSpinner={false}
                />
                <span className={styles.carNum}>{realNumber}</span>
              </div>
            </div>
          );
        })}
      />
    </div>
  );
};
